import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { BILLING_CYCLES, type BillingCycle } from "@/config/stripePlans";

interface SubscriptionState {
  subscriptionTier: string | null;
  billingCycleType: BillingCycle | null;
  nextBillingDate: string | null;
  subscriptionStatus: string | null;
  stripeCustomerId: string | null;
  loading: boolean;
}

export function useSubscription() {
  const { user, businessId } = useAuth();
  const [state, setState] = useState<SubscriptionState>({
    subscriptionTier: null,
    billingCycleType: null,
    nextBillingDate: null,
    subscriptionStatus: null,
    stripeCustomerId: null,
    loading: true,
  });

  const fetchSubscription = useCallback(async () => {
    if (!user || !businessId) {
      setState({
        subscriptionTier: null,
        billingCycleType: null,
        nextBillingDate: null,
        subscriptionStatus: null,
        stripeCustomerId: null,
        loading: false,
      });
      return;
    }

    try {
      const { data, error } = await supabase
        .from("businesses")
        .select("subscription_tier, subscription_status, billing_cycle_type, next_billing_date, stripe_customer_id")
        .eq("business_id", businessId)
        .maybeSingle();

      if (error) throw error;

      setState({
        subscriptionTier: data?.subscription_tier || null,
        billingCycleType: BILLING_CYCLES.find((cycle) => cycle === data?.billing_cycle_type) ?? null,
        nextBillingDate: data?.next_billing_date || null,
        subscriptionStatus: data?.subscription_status || null,
        stripeCustomerId: data?.stripe_customer_id || null,
        loading: false,
      });
    } catch (err) {
      console.error("Failed to fetch subscription:", err);
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [businessId, user]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const isSubscribed = state.subscriptionStatus === "active" || state.subscriptionStatus === "past_due" || state.subscriptionStatus === "trialing";

  return { ...state, isSubscribed, refresh: fetchSubscription };
}
